import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, Plus, Trash2, Edit, Eye, EyeOff, Upload, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [notes, setNotes] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [newCert, setNewCert] = useState({
    certificate_id: '',
    student_name: '',
    course: '',
    issue_date: '',
    grade: 'Completed',
    father_husband_name: '',
    duration: '',
    from_date: '',
    to_date: '',
  });
  const [activeTab, setActiveTab] = useState('certificates');
  
  // Notes states
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [noteFile, setNoteFile] = useState(null);
  const [noteData, setNoteData] = useState({
    title: '',
    description: '',
    allowed_emails: ''
  });

  useEffect(() => {
    if (isLoggedIn) {
      fetchCertificates();
      fetchLeads();
      fetchNotes();
    }
  }, [isLoggedIn]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      localStorage.setItem('admin_token', response.data.token);
      setIsLoggedIn(true);
      toast.success('Login successful!');
    } catch (error) {
      toast.error('Invalid credentials');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsLoggedIn(false);
    setCredentials({ username: '', password: '' });
  };

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API}/admin/certificates`);
      setCertificates(response.data);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  const handleAddCertificate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/certificates`, newCert);
      toast.success('Certificate added successfully!');
      setShowAddForm(false);
      setNewCert({
        certificate_id: '',
        student_name: '',
        course: '',
        issue_date: '',
        grade: 'Completed',
        father_husband_name: '',
        duration: '',
        from_date: '',
        to_date: '',
      });
      fetchCertificates();
    } catch (error) {
      toast.error('Error adding certificate');
    }
  };

  const handleDeleteCertificate = async (certId) => {
    if (window.confirm('Are you sure you want to delete this certificate?')) {
      try {
        await axios.delete(`${API}/admin/certificates/${certId}`);
        toast.success('Certificate deleted');
        fetchCertificates();
      } catch (error) {
        toast.error('Error deleting certificate');
      }
    }
  };

  const handleUpdateCertificate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/certificates/${editingCert.certificate_id}`, editingCert);
      toast.success('Certificate updated!');
      setEditingCert(null);
      fetchCertificates();
    } catch (error) {
      toast.error('Error updating certificate');
    }
  };

  // Notes functions
  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/admin/notes`);
      setNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const handleUploadNote = async (e) => {
    e.preventDefault();
    if (!noteFile) {
      toast.error('Please select a PDF file');
      return;
    }

    const formData = new FormData();
    formData.append('file', noteFile);
    formData.append('title', noteData.title);
    formData.append('description', noteData.description);
    formData.append('allowed_emails', noteData.allowed_emails);

    try {
      await axios.post(`${API}/admin/notes`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Note uploaded successfully!');
      setShowUploadForm(false);
      setNoteFile(null);
      setNoteData({ title: '', description: '', allowed_emails: '' });
      fetchNotes();
    } catch (error) {
      toast.error('Error uploading note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await axios.delete(`${API}/admin/notes/${noteId}`);
        toast.success('Note deleted');
        fetchNotes();
      } catch (error) {
        toast.error('Error deleting note');
      }
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="pt-16 md:pt-20 min-h-screen flex items-center justify-center bg-slate-50">
        <SEO title="Admin" description="Admin login" path="/admin" noindex />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md"
        >
          <div className="bg-cyan-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-cyan-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 text-center mb-6">Admin Login</h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Username</label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                className="bg-white border border-slate-200 rounded-lg px-4 py-3 w-full focus:ring-2 focus:ring-cyan-500 outline-none"
                required
                data-testid="admin-username-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="bg-white border border-slate-200 rounded-lg px-4 py-3 w-full focus:ring-2 focus:ring-cyan-500 outline-none pr-12"
                  required
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full px-8 py-3 font-bold transition-all"
              data-testid="admin-login-button"
            >
              Login
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">Admin Dashboard</h1>
          <button
            onClick={handleLogout}
            className="bg-slate-200 hover:bg-slate-300 text-slate-900 px-6 py-2 rounded-lg font-medium transition-all"
            data-testid="admin-logout-button"
          >
            Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-slate-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab('certificates')}
            className={`px-6 py-3 font-bold transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'certificates'
                ? 'border-cyan-600 text-cyan-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
            data-testid="tab-certificates"
          >
            Certificates ({certificates.length})
          </button>
          <button
            onClick={() => setActiveTab('leads')}
            className={`px-6 py-3 font-bold transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'leads'
                ? 'border-cyan-600 text-cyan-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
            data-testid="tab-leads"
          >
            Leads ({leads.length})
          </button>
          <button
            onClick={() => setActiveTab('notes')}
            className={`px-6 py-3 font-bold transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'notes'
                ? 'border-cyan-600 text-cyan-600'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
            data-testid="tab-notes"
          >
            Notes ({notes.length})
          </button>
        </div>

        {/* Certificates Tab */}
        {activeTab === 'certificates' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Manage Certificates</h2>
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 transition-all"
                data-testid="add-certificate-button"
              >
                <Plus size={20} />
                Add Certificate
              </button>
            </div>

            {/* Add/Edit Form */}
            {(showAddForm || editingCert) && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl p-6 shadow-sm mb-6"
              >
                <h3 className="text-xl font-bold text-slate-900 mb-4">
                  {editingCert ? 'Edit Certificate' : 'Add New Certificate'}
                </h3>
                <form onSubmit={editingCert ? handleUpdateCertificate : handleAddCertificate} className="grid md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Certificate ID (e.g., GA2024001)"
                    value={editingCert ? editingCert.certificate_id : newCert.certificate_id}
                    onChange={(e) =>
                      editingCert
                        ? setEditingCert({ ...editingCert, certificate_id: e.target.value })
                        : setNewCert({ ...newCert, certificate_id: e.target.value })
                    }
                    className="bg-white border border-slate-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-500 outline-none"
                    required
                    disabled={!!editingCert}
                    data-testid="cert-form-id"
                  />
                  <input
                    type="text"
                    placeholder="Student Name"
                    value={editingCert ? editingCert.student_name : newCert.student_name}
                    onChange={(e) =>
                      editingCert
                        ? setEditingCert({ ...editingCert, student_name: e.target.value })
                        : setNewCert({ ...newCert, student_name: e.target.value })
                    }
                    className="bg-white border border-slate-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-500 outline-none"
                    required
                    data-testid="cert-form-name"
                  />
                  <input
                    type="text"
                    placeholder="Father/Husband Name"
                    value={editingCert ? editingCert.father_husband_name : newCert.father_husband_name}
                    onChange={(e) =>
                      editingCert
                        ? setEditingCert({ ...editingCert, father_husband_name: e.target.value })
                        : setNewCert({ ...newCert, father_husband_name: e.target.value })
                    }
                    className="bg-white border border-slate-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-500 outline-none"
                    data-testid="cert-form-father-name"
                  />
                  <input
                    type="text"
                    placeholder="Course Name"
                    value={editingCert ? editingCert.course : newCert.course}
                    onChange={(e) =>
                      editingCert
                        ? setEditingCert({ ...editingCert, course: e.target.value })
                        : setNewCert({ ...newCert, course: e.target.value })
                    }
                    className="bg-white border border-slate-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-500 outline-none"
                    required
                    data-testid="cert-form-course"
                  />
                  <input
                    type="text"
                    placeholder="Grade (e.g., A+, Completed)"
                    value={editingCert ? editingCert.grade : newCert.grade}
                    onChange={(e) =>
                      editingCert
                        ? setEditingCert({ ...editingCert, grade: e.target.value })
                        : setNewCert({ ...newCert, grade: e.target.value })
                    }
                    className="bg-white border border-slate-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-500 outline-none"
                    data-testid="cert-form-grade"
                  />
                  <input
                    type="text"
                    placeholder="Duration (e.g., 3 Months)"
                    value={editingCert ? editingCert.duration : newCert.duration}
                    onChange={(e) =>
                      editingCert
                        ? setEditingCert({ ...editingCert, duration: e.target.value })
                        : setNewCert({ ...newCert, duration: e.target.value })
                    }
                    className="bg-white border border-slate-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-500 outline-none"
                    data-testid="cert-form-duration"
                  />
                  <input
                    type="text"
                    placeholder="From Date (DD/MM/YYYY)"
                    value={editingCert ? editingCert.from_date : newCert.from_date}
                    onChange={(e) =>
                      editingCert
                        ? setEditingCert({ ...editingCert, from_date: e.target.value })
                        : setNewCert({ ...newCert, from_date: e.target.value })
                    }
                    className="bg-white border border-slate-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-500 outline-none"
                    data-testid="cert-form-from-date"
                  />
                  <input
                    type="text"
                    placeholder="To Date (DD/MM/YYYY)"
                    value={editingCert ? editingCert.to_date : newCert.to_date}
                    onChange={(e) =>
                      editingCert
                        ? setEditingCert({ ...editingCert, to_date: e.target.value })
                        : setNewCert({ ...newCert, to_date: e.target.value })
                    }
                    className="bg-white border border-slate-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-500 outline-none"
                    data-testid="cert-form-to-date"
                  />
                  <input
                    type="text"
                    placeholder="Issue Date (DD/MM/YYYY)"
                    value={editingCert ? editingCert.issue_date : newCert.issue_date}
                    onChange={(e) =>
                      editingCert
                        ? setEditingCert({ ...editingCert, issue_date: e.target.value })
                        : setNewCert({ ...newCert, issue_date: e.target.value })
                    }
                    className="bg-white border border-slate-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-500 outline-none"
                    required
                    data-testid="cert-form-issue-date"
                  />
                  <div className="flex gap-2 md:col-span-2">
                    <button
                      type="submit"
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-bold transition-all"
                      data-testid="cert-form-submit"
                    >
                      {editingCert ? 'Update' : 'Add'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddForm(false);
                        setEditingCert(null);
                      }}
                      className="bg-slate-200 hover:bg-slate-300 text-slate-900 px-6 py-2 rounded-lg font-medium transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Certificates List */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {certificates.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  No certificates added yet. Click "Add Certificate" to create one.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Certificate ID</th>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Student Name</th>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Course</th>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Issue Date</th>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Grade</th>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {certificates.map((cert) => (
                        <tr key={cert.certificate_id} className="hover:bg-slate-50">
                          <td className="px-6 py-4 text-sm font-medium text-slate-900">{cert.certificate_id}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{cert.student_name}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{cert.course}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{cert.issue_date}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{cert.grade}</td>
                          <td className="px-6 py-4 text-sm">
                            <div className="flex gap-2">
                              <button
                                onClick={() => setEditingCert(cert)}
                                className="text-blue-600 hover:text-blue-700"
                                data-testid={`edit-cert-${cert.certificate_id}`}
                              >
                                <Edit size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteCertificate(cert.certificate_id)}
                                className="text-red-600 hover:text-red-700"
                                data-testid={`delete-cert-${cert.certificate_id}`}
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Enquiries / Leads</h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {leads.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  No leads yet. They will appear here when students submit the contact form.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Name</th>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Phone</th>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Course</th>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Message</th>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {leads.map((lead) => (
                        <tr key={lead.id} className="hover:bg-slate-50">
                          <td className="px-6 py-4 text-sm font-medium text-slate-900">{lead.name}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            <a href={`tel:${lead.phone}`} className="text-cyan-600 hover:text-cyan-700">
                              {lead.phone}
                            </a>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600">{lead.course}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{lead.message || '-'}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            {new Date(lead.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notes Tab */}
        {activeTab === 'notes' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Manage Study Notes</h2>
              <button
                onClick={() => setShowUploadForm(!showUploadForm)}
                className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2 transition-all"
                data-testid="upload-note-button"
              >
                <Upload size={20} />
                Upload Note
              </button>
            </div>

            {/* Upload Form */}
            {showUploadForm && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl p-6 shadow-sm mb-6"
              >
                <h3 className="text-xl font-bold text-slate-900 mb-4">Upload New Note</h3>
                <form onSubmit={handleUploadNote} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Note Title *</label>
                    <input
                      type="text"
                      value={noteData.title}
                      onChange={(e) => setNoteData({ ...noteData, title: e.target.value })}
                      className="bg-white border border-slate-200 rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-cyan-500 outline-none"
                      required
                      placeholder="e.g., MS Excel - Chapter 1"
                      data-testid="note-title-input"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                    <textarea
                      value={noteData.description}
                      onChange={(e) => setNoteData({ ...noteData, description: e.target.value })}
                      className="bg-white border border-slate-200 rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-cyan-500 outline-none"
                      rows="3"
                      placeholder="Brief description of the note content"
                      data-testid="note-description-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Allowed Emails * (comma-separated)</label>
                    <textarea
                      value={noteData.allowed_emails}
                      onChange={(e) => setNoteData({ ...noteData, allowed_emails: e.target.value })}
                      className="bg-white border border-slate-200 rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-cyan-500 outline-none"
                      rows="3"
                      required
                      placeholder="student1@email.com, student2@email.com, student3@email.com"
                      data-testid="note-emails-input"
                    />
                    <p className="text-sm text-slate-500 mt-1">Only these email addresses will be able to access this note</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">PDF File *</label>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setNoteFile(e.target.files[0])}
                      className="bg-white border border-slate-200 rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-cyan-500 outline-none"
                      required
                      data-testid="note-file-input"
                    />
                    <p className="text-sm text-slate-500 mt-1">Only PDF files are allowed</p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-bold transition-all"
                      data-testid="submit-note-button"
                    >
                      Upload Note
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowUploadForm(false);
                        setNoteFile(null);
                        setNoteData({ title: '', description: '', allowed_emails: '' });
                      }}
                      className="bg-slate-200 hover:bg-slate-300 text-slate-900 px-6 py-2 rounded-lg font-medium transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Notes List */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {notes.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  No notes uploaded yet. Click "Upload Note" to add study materials.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Title</th>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Description</th>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Filename</th>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Allowed Emails</th>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Uploaded</th>
                        <th className="px-6 py-3 text-left text-sm font-bold text-slate-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {notes.map((note) => (
                        <tr key={note.note_id} className="hover:bg-slate-50">
                          <td className="px-6 py-4 text-sm font-medium text-slate-900">{note.title}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{note.description || '-'}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            <div className="flex items-center gap-2">
                              <FileText size={16} className="text-red-500" />
                              {note.filename}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            <div className="max-w-xs truncate" title={note.allowed_emails.join(', ')}>
                              {note.allowed_emails.length} email(s)
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            {new Date(note.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <button
                              onClick={() => handleDeleteNote(note.note_id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-note-${note.note_id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;